import React from "react";
import { ShieldCheck, Lock, Eye, KeyRound, Ban, Server, CheckCircle2 } from "lucide-react";
import { LegalPageLayout, LegalSection } from "../../components/LegalPageLayout";

const sections = [
  { id: "principles", label: "Security principles" },
  { id: "oauth", label: "Tesla OAuth authentication" },
  { id: "encryption", label: "Encryption" },
  { id: "readonly", label: "Read-only access" },
  { id: "no-passwords", label: "We never see your Tesla password" },
  { id: "infrastructure", label: "Infrastructure" },
  { id: "disclosure", label: "Responsible disclosure" },
];

export default function Security() {
  return (
    <LegalPageLayout
      eyebrow="Trust · Security"
      title="Security at EVIQ"
      updated="February 20, 2026"
      intro="EVIQ is built around your Tesla data. That comes with an obligation to protect it. This page describes how we authenticate you, what we can and can't do to your vehicle, how your data is encrypted, and how to report a vulnerability."
      sections={sections}
    >
      <LegalSection id="principles" title="1. Security principles">
        <p>Four principles drive every technical decision at EVIQ:</p>
        <ol>
          <li><strong>Least privilege.</strong> We ask for the smallest possible Tesla scope needed to compute your EVIQ Score™ — and nothing more.</li>
          <li><strong>Encryption everywhere.</strong> TLS 1.2+ in transit; AES-256 at rest for any token we must persist.</li>
          <li><strong>Read-only by design.</strong> The application binary literally does not import any Tesla command endpoints. See <a href="#readonly">§4</a>.</li>
          <li><strong>Owner override.</strong> Revoke access in one click. Delete your account in one email.</li>
        </ol>
      </LegalSection>

      <LegalSection id="oauth" title="2. Tesla OAuth authentication">
        <p>
          EVIQ uses <strong>Tesla OAuth 2.0 with PKCE</strong>. When you click &ldquo;Sign
          in with Tesla,&rdquo; EVIQ redirects your browser to Tesla&rsquo;s own
          authentication server at <code>auth.tesla.com</code>. You enter your Tesla
          credentials directly with Tesla — EVIQ&rsquo;s code path is never in the loop.
        </p>
        <p>
          When you approve access, Tesla redirects your browser back to our OAuth callback
          at <code>eviq.pro/api/tesla/callback</code> with a short-lived authorization
          code. Our server exchanges the code (server-to-server) for an access token and
          a refresh token. Only the refresh token is persisted, and it&rsquo;s encrypted
          before it touches disk.
        </p>
        <p>
          You can revoke EVIQ&rsquo;s access at any time from{" "}
          <a href="https://accounts.tesla.com/en_US/account-settings/security" target="_blank" rel="noreferrer">
            Tesla → Account &amp; Security → Third-Party Apps
          </a>, or by clicking &ldquo;Disconnect Tesla&rdquo; in EVIQ. Revocation is
          honored immediately by Tesla; we honor it on our next scheduled sync.
        </p>
      </LegalSection>

      <LegalSection id="encryption" title="3. Encryption">
        <ul>
          <li><strong>In transit:</strong> TLS 1.2 or higher for every connection between your browser, EVIQ&rsquo;s servers, Tesla, Stripe, and every third-party service listed in our <a href="/privacy">Privacy Policy</a>.</li>
          <li><strong>At rest:</strong> Refresh tokens are encrypted with AES-256-GCM using a key stored in a separate, access-audited secret manager. Our database provider additionally encrypts underlying disk volumes.</li>
          <li><strong>Payment data:</strong> Card numbers, CVCs, and expiration dates never touch EVIQ&rsquo;s infrastructure. Payment fields render inside our processor&rsquo;s (Stripe) PCI-DSS-compliant iframe.</li>
        </ul>
      </LegalSection>

      <LegalSection id="readonly" title="4. Read-only access — no vehicle control">
        <p>
          EVIQ requests <strong>read-only</strong> Tesla scopes. The application does not
          include, import, or otherwise link the Tesla Command Signing library, and it
          does not call any command endpoint. Concretely, EVIQ <em>cannot</em>:
        </p>
        <ul>
          <li>Unlock, lock, or open the trunk/frunk of your vehicle.</li>
          <li>Start, stop, or throttle charging.</li>
          <li>Precondition the cabin.</li>
          <li>Honk, flash lights, or summon.</li>
          <li>Change any setting, name, or Autopilot preference on your car.</li>
        </ul>
        <p>
          EVIQ can only <em>read</em> the vehicle information you approved during OAuth —
          identity (VIN/model/year), battery diagnostics, charging session history, and
          odometer.
        </p>
      </LegalSection>

      <LegalSection id="no-passwords" title="5. We never see your Tesla password">
        <p>
          Tesla&rsquo;s OAuth flow is designed specifically to keep third-party
          applications like EVIQ out of the password handoff. Your password goes from
          your browser directly to <code>auth.tesla.com</code> — it never transits, and
          is never seen by, EVIQ.
        </p>
      </LegalSection>

      <LegalSection id="infrastructure" title="6. Infrastructure">
        <ul>
          <li>Application hosted on a SOC 2-audited Kubernetes platform with automatic patching and isolated tenant workloads.</li>
          <li>Database on MongoDB Atlas with disk-level encryption, private networking, and automated backups.</li>
          <li>Secrets managed via a dedicated secret store, not committed to source.</li>
          <li>Deploys are immutable and versioned; rollback is a one-click operation.</li>
        </ul>
      </LegalSection>

      <LegalSection id="disclosure" title="7. Responsible disclosure">
        <p>
          Found a vulnerability? Please email{" "}
          <a href="mailto:support@eviq.pro">support@eviq.pro</a> with subject
          &ldquo;<strong>Security disclosure</strong>&rdquo;. We ask that you:
        </p>
        <ul>
          <li>Give us reasonable time (typically 90 days) to remediate before public disclosure.</li>
          <li>Do not attempt to access, modify, or destroy any user data that is not your own.</li>
          <li>Do not perform any denial-of-service tests against our production environment.</li>
        </ul>
        <p>
          We do not currently offer a paid bug-bounty, but we credit reporters in our
          release notes (with their consent) and are happy to write a reference letter.
        </p>
      </LegalSection>

      {/* Guarantee cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        <SecCard icon={Lock} title="TLS 1.2+ everywhere" body="Every request between you, EVIQ, Tesla, and Stripe is encrypted in transit." />
        <SecCard icon={KeyRound} title="AES-256 tokens" body="Tesla refresh tokens are encrypted before they ever hit disk." />
        <SecCard icon={Eye} title="Read-only scopes" body="We literally cannot control your car — the code path doesn't exist." />
        <SecCard icon={Ban} title="No passwords stored" body="Your Tesla password is entered directly with Tesla. EVIQ never sees it." />
        <SecCard icon={Server} title="SOC 2 hosting" body="Infrastructure runs on an audited Kubernetes platform with immutable deploys." />
        <SecCard icon={ShieldCheck} title="One-click revoke" body="Disconnect Tesla in EVIQ or in your Tesla account — instant honor." />
      </div>
    </LegalPageLayout>
  );
}

const SecCard = ({ icon: Icon, title, body }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
    <div className="flex items-start gap-3">
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 border border-white/10"
        style={{ background: "rgba(0,229,255,0.08)" }}
      >
        <Icon className="w-4 h-4" style={{ color: "#00E5FF" }} strokeWidth={2} />
      </div>
      <div className="min-w-0">
        <h3 className="text-white font-semibold text-sm mb-1">{title}</h3>
        <p className="text-xs text-zinc-400 leading-relaxed">{body}</p>
      </div>
    </div>
  </div>
);
